import { useState, type ReactNode } from "react";
import { MessageSquare } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ClassificationBadge } from "@/components/ui/classification-badge";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { RiskLevelBadge } from "@/components/ui/risk-level-badge/RiskLevelBadge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import type { ActualizeazaTargetareTintaRequest } from "@/api/types/targetareTinte";
import type { TargetareCampanie, TargetareStareTinta } from "@/types/targetare";
import { formatDataOra, formatDimensiune } from "@/utils/targetareFormatting";
import type { RandTinta } from "@/utils/targetareRaportTinte";
import { riskLevelDinNivel } from "@/utils/targetareRisc";
import type { ColoanaTabel } from "@/utils/targetareTabel";
import {
  CLASE_STARE_TINTA,
  ETICHETE_PRIORITATE,
  ETICHETE_STARE_TINTA,
  STARI_TINTA,
} from "@/utils/targetareTinte";
import { TierBadge } from "../TierBadge";

export type ContextTinte = {
  campanii: TargetareCampanie[];
  onActualizeaza: (patch: ActualizeazaTargetareTintaRequest) => void;
  onFisa: (cui: string) => void;
};

const FARA_CAMPANIE = "fara";

function NotaTinta({
  tinta,
  onSalveaza,
}: {
  tinta: RandTinta;
  onSalveaza: (note: string) => void;
}) {
  const [text, setText] = useState(tinta.note ?? "");
  return (
    <Popover onOpenChange={(v) => v && setText(tinta.note ?? "")}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex max-w-[200px] items-center gap-1 truncate rounded px-1 text-left text-xs hover:bg-muted",
            tinta.note ? "text-foreground" : "text-muted-foreground"
          )}
          title={tinta.note ?? "Adaugă o notă"}
          onClick={(e) => e.stopPropagation()}
        >
          <MessageSquare className="h-3 w-3 shrink-0" />
          <span className="truncate">{tinta.note ?? "notă…"}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 space-y-2"
        onClick={(e) => e.stopPropagation()}
      >
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          placeholder="Ce s-a discutat, ce urmează…"
        />
        <Button size="sm" className="w-full" onClick={() => onSalveaza(text)}>
          Salvează nota
        </Button>
      </PopoverContent>
    </Popover>
  );
}

const opreste = (e: { stopPropagation: () => void }) => e.stopPropagation();

export function celulaTinta(ctx: ContextTinte) {
  return (coloana: ColoanaTabel<RandTinta>, r: RandTinta): ReactNode => {
    const v = r[coloana.id];
    switch (coloana.id) {
      case "denumire":
        return (
          <button
            type="button"
            className="max-w-full truncate text-left font-medium hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              ctx.onFisa(r.cui);
            }}
            title={r.denumire ?? ""}
          >
            {r.denumire ?? "(firmă necunoscută)"}
          </button>
        );
      case "cui":
        return <span className="font-mono text-xs">{r.cui}</span>;
      case "stare":
        return (
          <div onClick={opreste}>
            <Select
              value={r.stare}
              onValueChange={(s) =>
                ctx.onActualizeaza({
                  id: r.id,
                  stare: s as TargetareStareTinta,
                })
              }
            >
              <SelectTrigger
                className={cn(
                  "h-7 w-[140px] text-xs",
                  CLASE_STARE_TINTA[r.stare]
                )}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STARI_TINTA.map((s) => (
                  <SelectItem key={s} value={s}>
                    {ETICHETE_STARE_TINTA[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      case "prioritate_text":
        return (
          <div onClick={opreste}>
            <Select
              value={String(r.prioritate)}
              onValueChange={(p) =>
                ctx.onActualizeaza({ id: r.id, prioritate: Number(p) })
              }
            >
              <SelectTrigger className="h-7 w-[110px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3].map((p) => (
                  <SelectItem key={p} value={String(p)}>
                    {ETICHETE_PRIORITATE[p]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      case "responsabil":
        return (
          <Input
            key={`${r.id}-${r.responsabil ?? ""}`}
            defaultValue={r.responsabil ?? ""}
            placeholder="–"
            className="h-7 w-[120px] text-xs"
            onClick={opreste}
            onBlur={(e) => {
              const val = e.target.value.trim();
              if (val !== (r.responsabil ?? ""))
                ctx.onActualizeaza({ id: r.id, responsabil: val });
            }}
          />
        );
      case "urmatoarea_actiune_la":
        return (
          <Input
            type="date"
            value={r.urmatoarea_actiune_la ?? ""}
            className="h-7 w-[140px] text-xs"
            onClick={opreste}
            onChange={(e) =>
              ctx.onActualizeaza({
                id: r.id,
                urmatoarea_actiune_la: e.target.value || null,
              })
            }
          />
        );
      case "campanie_nume":
        return (
          <div onClick={opreste}>
            <Select
              value={
                r.campanie_id === null ? FARA_CAMPANIE : String(r.campanie_id)
              }
              onValueChange={(c) =>
                ctx.onActualizeaza({
                  id: r.id,
                  campanie_id: c === FARA_CAMPANIE ? null : Number(c),
                })
              }
            >
              <SelectTrigger className="h-7 w-[160px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={FARA_CAMPANIE}>Fără campanie</SelectItem>
                {ctx.campanii.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>
                    {c.nume}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      case "note":
        return (
          <NotaTinta
            tinta={r}
            onSalveaza={(note) => ctx.onActualizeaza({ id: r.id, note })}
          />
        );
      case "tier":
        return <TierBadge tier={r.tier} />;
      case "clasificare_estimata":
        return (
          <ClassificationBadge
            classification={r.clasificare_estimata}
            size="sm"
          />
        );
      case "nivel_risc":
        return r.nivel_risc ? (
          <RiskLevelBadge riskLevel={riskLevelDinNivel(r.nivel_risc)} />
        ) : (
          ""
        );
      case "dimensiune_stricta":
        return formatDimensiune(r.dimensiune_stricta);
      case "sursa":
        return (
          <Badge variant="outline" className="font-normal">
            {r.sursa}
          </Badge>
        );
      case "creat_la":
      case "actualizat_la":
        return (
          <span className="text-xs">{formatDataOra(v as string | null)}</span>
        );
      case "email_principal":
      case "telefon_principal":
      case "site":
      case "email_persoana":
        return <span className="font-mono text-xs">{String(v ?? "")}</span>;
      default:
        break;
    }
    if (Array.isArray(v)) return v.join(", ");
    return (
      <span title={String(v ?? "")} className="block truncate">
        {String(v ?? "")}
      </span>
    );
  };
}
